import { AUTH_ERROR_REASONS } from '../auth/authErrorMessage';
import type { InvitationHandoffEvent } from '../auth/invitationHandoffEvents';

/**
 * The bounded property vocabulary for the invitation funnel.
 *
 * The allowlist exists because these events fire on the auth callback route,
 * whose URL carries a Stytch magic-link token. Anything not named here is
 * rejected rather than sent, so a future caller cannot widen the payload by
 * accident. Enum rules keep that guarantee while allowing more than a flag: the
 * accepted values are fixed strings, so no caller-supplied identifier can ride
 * along inside one.
 */
type PropertyRule =
  | { readonly type: 'boolean' }
  | { readonly type: 'enum'; readonly values: readonly string[] };

/** Where in the console an offer was shown when it was acted on. */
export const OFFER_PLACEMENTS = ['welcome', 'empty_org', 'gutter'] as const;
export type OfferPlacement = (typeof OFFER_PLACEMENTS)[number];

/** What the invitee opened first. Deliberately a category, never an id. */
export const FIRST_OPEN_KINDS = ['document', 'tracker', 'request'] as const;
export type FirstOpenKind = (typeof FIRST_OPEN_KINDS)[number];

const placementRule = { type: 'enum', values: OFFER_PLACEMENTS } as const;
const firstOpenRule = { type: 'enum', values: FIRST_OPEN_KINDS } as const;
/**
 * Why a landing failed. An enum rather than the raw Stytch `error_type` string:
 * the list is the one this app already recognizes, so an unrecognized failure
 * reports as `unrecognized` instead of carrying an unbounded value off the
 * token-bearing callback URL.
 */
const authErrorReasonRule = { type: 'enum', values: AUTH_ERROR_REASONS } as const;

/**
 * The funnel after the desktop interstitial was retired.
 *
 * `invite_handoff_shown` and `invite_browser_instead_chosen` are gone with the
 * card and the escape hatch they measured — with the browser as the default
 * landing, "chose the browser instead" can only ever be zero. `auto` is gone
 * from the deep-link event for the same reason: nothing navigates to
 * `nimbalyst://` on the invitee's behalf any more, so every follow is manual.
 *
 * `invite_first_open` is the event that replaces them, and the only one that
 * answers whether the landing actually bought anything: did the new teammate
 * open something real in their first session?
 */
const INVITATION_EVENT_SCHEMAS = {
  invite_landing_viewed: {},
  invite_landing_failed: { reason: authErrorReasonRule, recovered: { type: 'boolean' } },
  invite_deep_link_followed: { placement: placementRule },
  invite_download_clicked: { placement: placementRule },
  invite_first_open: { kind: firstOpenRule },
} as const satisfies Record<InvitationHandoffEvent, Record<string, PropertyRule>>;

const CALLBACK_URL_PROPERTIES = new Set([
  '$current_url',
  '$pathname',
  '$referrer',
  '$referring_domain',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
  '$initial_referring_domain',
]);

/** The URL/referrer defaults that could carry a Stytch magic-link token. */
export const invitationAnalyticsPropertyDenylist = [...CALLBACK_URL_PROPERTIES];

function assertValue(event: string, key: string, rule: PropertyRule, value: unknown): void {
  if (rule.type === 'boolean') {
    if (typeof value !== 'boolean') throw new Error(`${event}.${key} must be boolean`);
    return;
  }
  if (typeof value !== 'string' || !rule.values.includes(value)) {
    throw new Error(`${event}.${key} must be one of ${rule.values.join(', ')}`);
  }
}

export function buildInvitationAnalyticsCapture(
  event: InvitationHandoffEvent,
  properties: Record<string, unknown>,
): { event: InvitationHandoffEvent; properties: Record<string, boolean | string> } {
  const schema = INVITATION_EVENT_SCHEMAS[event];
  for (const [key, value] of Object.entries(properties)) {
    const rule = (schema as Record<string, PropertyRule>)[key];
    if (!rule) throw new Error(`${event}.${key} is not an allowlisted property`);
    assertValue(event, key, rule, value);
  }
  return { event, properties: { surface: 'web_console', ...properties } as Record<string, boolean | string> };
}

/** Defense in depth for properties PostHog adds around the bounded event body. */
export function stripInvitationCallbackProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !CALLBACK_URL_PROPERTIES.has(key)),
  );
}
